import { useEffect, useRef, useState } from 'react';
import ePub, { Book as EPUBBook, Rendition } from 'epubjs';
import { Book, HighlightColor } from '../../types';
import { useStore, TocItem } from '../../store/useStore';
import { useSwipeable } from 'react-swipeable';
import { getEPUBFile } from '../../utils/fileStorage';
import TextSelectionMenu from './TextSelectionMenu';
import NoteModal from './NoteModal';
import AIAssistant from '../AI/AIAssistant';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// 전역 타입 선언
export interface EPUBSearchMatch {
  cfi: string;       // spine href — epubGoTo(href)로 이동 가능
  text: string;
  excerpt: string;
  pageNumber?: number; // 책넘김 모드에서만 채워진다(Reader.tsx가 등록하는 구현)
}

declare global {
  interface Window {
    epubNextPage?: () => void;
    epubPrevPage?: () => void;
    epubGoTo?: (href: string) => void;
    epubGetCurrentText?: () => string;
    // PM 결정(2026-09-14): EPUB도 PDF(S2-2)처럼 책 전체 검색을 지원한다.
    // 결과의 cfi에는 그 대목이 있는 spine href를 실어 보내 epubGoTo로 바로 이동한다.
    epubSearchAllPages?: (query: string) => Promise<EPUBSearchMatch[]>;
    epubCurrentPage?: number;
    epubTotalPages?: number;
  }
}

interface EPUBViewerProps {
  book: Book;
  onLocationChange: (location: string) => void;
}

// Readium CSS §3.4(내비게이션 활자 규칙 적용 범위) — body만으로는 출판사 CSS가
// 태그를 직접 겨냥할 때 무력화되므로 명시적으로 겹쳐 지정한다.
const TEXT_FLOW_SELECTOR = 'p, div, span, li, td, blockquote, h1, h2, h3, h4, h5, h6';

const EPUBViewer = ({ book, onLocationChange }: EPUBViewerProps) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const renditionRef = useRef<Rendition | null>(null);
  const bookRef = useRef<EPUBBook | null>(null);
  // PM 결정(2026-09-14): 챕터(spine item)별 추출 텍스트 캐시 — 검색할 때마다
  // 책 전체를 다시 load()하지 않도록 첫 조회 결과를 href로 쌓아 둔다.
  const epubTextCacheRef = useRef<Map<string, string>>(new Map());
  const isInitializedRef = useRef(false);

  const { readerSettings, updateBook, addHighlight, addNote, setTocItems, clearToc, showTOC, toggleTOC } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 딥QA 수정(2026-09-14): epub.js 교착(아래 ⓪) 감지 시 인스턴스를 통째로 다시 만든다.
  // 값이 바뀌면 로딩 effect가 cleanup(destroy) 후 재실행된다.
  const [reopenAttempt, setReopenAttempt] = useState(0);
  // 딥QA 재점검(2026-09-14): 책이 바뀌면 재시도 횟수를 0으로 되돌린다. 안 그러면
  // 앞 책에서 재시도를 다 쓴 경우 다음 책은 한 번도 재시도하지 못하고 곧장 에러로 간다.
  useEffect(() => {
    setReopenAttempt(0);
  }, [book.id, book.filePath]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [locationsReady, setLocationsReady] = useState(false);

  // Text selection state
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const [selectionMenuPos, setSelectionMenuPos] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [selectedCfiRange, setSelectedCfiRange] = useState('');

  // Modals
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiAction, setAIAction] = useState<'ask' | 'summarize' | 'translate' | 'explain'>('ask');

  // Swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => renditionRef.current?.next(),
    onSwipedRight: () => renditionRef.current?.prev(),
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // B4: 입력 요소(검색창 등)에 포커스가 있으면 단축키를 발동하지 않는다 — FlipbookView와 동일 가드
      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTypingTarget) return;

      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        renditionRef.current?.next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        renditionRef.current?.prev();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // ✅ Ensure viewerRef is available (may not be on first render)
  const [viewerReady, setViewerReady] = useState(false);
  useEffect(() => {
    if (viewerRef.current && !viewerReady) {
      setViewerReady(true);
    }
  });

  // ✅ EPUB 로딩
  useEffect(() => {
    if (!viewerRef.current) {
      console.log('⚠️ viewerRef 아직 준비 안됨, 대기 중...');
      return;
    }

    if (!book.filePath) {
      console.log('⚠️ filePath 없음');
      return;
    }

    if (isInitializedRef.current) {
      console.log('⚠️ 이미 초기화됨, 스킵');
      return;
    }

    isInitializedRef.current = true;

    const initEPUB = async () => {
      try {
        console.log('═══════════════════════════════════════');
        console.log('1️⃣ EPUB 로드 시작:', book.title);

        const blob = await getEPUBFile(book.filePath);
        if (!blob) {
          throw new Error('EPUB 파일을 찾을 수 없습니다');
        }
        console.log('2️⃣ Blob 로드 완료:', (blob.size / 1024 / 1024).toFixed(2), 'MB');

        const arrayBuffer = await blob.arrayBuffer();
        console.log('3️⃣ ArrayBuffer 변환 완료');

        const epubBook = ePub(arrayBuffer);
        bookRef.current = epubBook;
        console.log('4️⃣ ePub 인스턴스 생성 완료');

        const container = viewerRef.current;
        if (!container) {
          throw new Error('뷰어 컨테이너를 찾을 수 없습니다');
        }
        console.log('5️⃣ 컨테이너 크기:', container.clientWidth, 'x', container.clientHeight);

        const rendition = epubBook.renderTo(container, {
          width: '100%',
          height: '100%',
          spread: 'none',
          flow: 'paginated',
          allowScriptedContent: true,
        });
        renditionRef.current = rendition;

        // ✅ 전역 함수 등록
        window.epubNextPage = () => rendition.next();
        window.epubPrevPage = () => rendition.prev();
        window.epubGoTo = (href: string) => rendition.display(href);
        
        // TTS용 현재 페이지 텍스트 가져오기
        window.epubGetCurrentText = () => {
          try {
            const contents = (rendition as any).getContents();
            let text = '';
            if (Array.isArray(contents)) {
              contents.forEach((content: any) => {
                if (content.document?.body) {
                  text += content.document.body.innerText + '\n';
                }
              });
            }
            return text.trim();
          } catch (e) {
            console.error('텍스트 가져오기 실패:', e);
            return '';
          }
        };
        // PM 결정(2026-09-14): 책 전체 검색. 종전엔 SearchPanel이 epubGetCurrentText()로
        // "지금 렌더된 챕터" 하나만 훑어서, 다른 챕터에 있는 단어는 검색해도 안 나왔다
        // (PDF는 S2-2에서 이미 해결, EPUB만 남아 있던 같은 계열 결함).
        epubTextCacheRef.current = new Map(); // 새 책이면 이전 책 텍스트 캐시 폐기
        window.epubSearchAllPages = async (query: string) => {
          const results: EPUBSearchMatch[] = [];
          if (!query.trim()) return results;

          const MAX_RESULTS = 50;
          // 검색어는 항상 리터럴로 — PDF 쪽(c2a4067)과 같은 ReDoS 방어.
          const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          let regex: RegExp;
          try {
            regex = new RegExp(escapeRegExp(query), 'gi');
          } catch {
            return results;
          }

          const spineItems: any[] = (epubBook.spine as any)?.items || [];
          const cache = epubTextCacheRef.current;

          for (const item of spineItems) {
            if (results.length >= MAX_RESULTS) break;
            const href: string = item.href;
            let text = cache.get(href);
            if (text === undefined) {
              try {
                const doc: any = await epubBook.load(item.href);
                const extracted: string = String(doc?.body?.textContent || '').replace(/\s+/g, ' ').trim();
                text = extracted;
                cache.set(href, extracted); // 성공했을 때만 캐시 — 실패는 다음 검색에서 재시도
              } catch (e) {
                console.error(`EPUB 검색: ${href} 로드 실패:`, e);
                text = '';
              }
            }
            if (!text) continue;

            regex.lastIndex = 0;
            let match: RegExpExecArray | null;
            while ((match = regex.exec(text)) !== null && results.length < MAX_RESULTS) {
              const start = Math.max(0, match.index - 30);
              const end = Math.min(text.length, match.index + query.length + 30);
              results.push({
                cfi: href,
                text: match[0],
                excerpt: `...${text.substring(start, end)}...`,
              });
              if (match.index === regex.lastIndex) regex.lastIndex++;
            }
          }

          return results;
        };
        console.log('   ✅ 전역 함수 등록 완료');

        // ✅ 목차 로드
        epubBook.loaded.navigation.then((nav: any) => {
          console.log('📚 목차 로드:', nav.toc);
          
          const convertToc = (items: any[]): TocItem[] => {
            return items.map((item, index) => ({
              id: item.id || `toc-${index}`,
              label: item.label || '제목 없음',
              href: item.href || '',
              subitems: item.subitems?.length ? convertToc(item.subitems) : undefined,
            }));
          };
          
          if (nav.toc && nav.toc.length > 0) {
            setTocItems(convertToc(nav.toc), 'epub');
            // 목차가 있으면 자동으로 TOC 패널 열기 (첫 로드 시)
            if (!showTOC) {
              toggleTOC();
            }
            console.log('   ✅ 목차 저장 + 자동 열기:', nav.toc.length, '개 항목');
          }
        }).catch((err: any) => {
          console.warn('목차 로드 실패:', err);
        });

        // ✅ 전체 페이지 수 계산 (locations 생성 — 한번만, 정확하게)
        epubBook.ready.then(() => {
          console.log('6️⃣ EPUB ready, locations 생성 중...');

          // 600자 기준으로 locations 생성 (한국어 기준 자연스러운 1페이지 분량)
          // 1600자는 너무 커서 페이지 수가 적게 나옴 (타사 대비 절반)
          epubBook.locations.generate(600).then(() => {
            const totalLocations = epubBook.locations.length();
            if (totalLocations > 0) {
              setTotalPages(totalLocations);
              setLocationsReady(true);
              window.epubTotalPages = totalLocations;
              updateBook(book.id, { totalPages: totalLocations });

              // ✅ 현재 위치로 즉시 페이지 번호 계산 (1페이지에서도 표시되도록)
              try {
                const loc = rendition.currentLocation() as any;
                if (loc?.start) {
                  const pct = loc.start.percentage * 100;
                  const page = Math.max(1, Math.round((pct / 100) * totalLocations));
                  setCurrentPage(page);
                  window.epubCurrentPage = page;
                }
              } catch { /* 무시 */ }

              console.log('   ✅ 전체 페이지 확정:', totalLocations);
            } else {
              // fallback: spine 기반 추정
              const spineLength = (epubBook.spine as any)?.length || 1;
              const fallbackPages = spineLength * 10;
              setTotalPages(fallbackPages);
              setLocationsReady(true);
              window.epubTotalPages = fallbackPages;
              updateBook(book.id, { totalPages: fallbackPages });
              console.log('   ⚠️ locations 0, spine 기반 추정:', fallbackPages);
            }
          }).catch((err: any) => {
            console.warn('locations 생성 실패:', err);
            // fallback
            const spineLength = (epubBook.spine as any)?.length || 1;
            const fallbackPages = spineLength * 10;
            setTotalPages(fallbackPages);
            setLocationsReady(true);
            window.epubTotalPages = fallbackPages;
          });
        });

        applyReaderSettings(rendition);

        // ★ 딥QA 수정(2026-09-14) ⓪ — "책을 불러오는 중…" 무한 스피너의 진짜 원인.
        //   epub.js는 리소스 치환 체인(resources.replacements()/replaceCss())이
        //   실패하면 console.error만 찍고 book.opening을 영원히 미해결로 둔다:
        //
        //     // epubjs/lib/book.js:507-511
        //     this.replacements().then(() => {
        //       this.loaded.displayOptions.then(() => this.opening.resolve(this));
        //     }).catch(err => { console.error(err); });   // ← resolve/reject 둘 다 없음
        //
        //   그런데 rendition.display()는 book.opened를 큐로 기다리므로
        //   (epubjs/lib/rendition.js:161 `q.enqueue(this.book.opened)`) 한 번 이 상태에
        //   빠지면 iframe조차 만들어지지 않고 영영 안 열린다(90초 대기해도 동일, 실측).
        //   opening만 대신 resolve해 봤지만 그것으로는 부족했다(실측): 치환 체인 안에
        //   영원히 pending인 deferred가 남아 있어 그 인스턴스는 회복되지 않는다.
        //   그래서 교착을 감지하면 인스턴스를 버리고 처음부터 다시 만든다 — 실패가
        //   일시적이면(대개 그렇다) 재시도에서 정상적으로 열린다.
        const OPEN_TIMEOUT_MS = 8000;
        const MAX_REOPEN = 2;
        const openedInTime = await Promise.race([
          epubBook.opened.then(() => true),
          new Promise<boolean>((resolve) => setTimeout(() => resolve(false), OPEN_TIMEOUT_MS)),
        ]);

        if (!openedInTime) {
          if (reopenAttempt < MAX_REOPEN) {
            console.warn(`⚠️ book.opened 교착 감지 — 인스턴스를 버리고 재시도한다 (${reopenAttempt + 1}/${MAX_REOPEN})`);
            isInitializedRef.current = false;
            try { rendition.destroy(); } catch { /* noop */ }
            try { epubBook.destroy(); } catch { /* noop */ }
            setReopenAttempt((n) => n + 1); // effect 재실행 → 처음부터 다시
            return;
          }
          throw new Error('책을 여는 데 실패했습니다.\n잠시 후 다시 시도하거나, 탭을 새로 열어 주세요.');
        }

        // ★ 딥QA 수정(2026-09-14) ①: spine이 채워지기 전에 display()를 부르면
        //   firstHref가 undefined가 된다(로그에 "Display 처음(표지): default").
        //   spine만 좁게 기다린다 — ready/opened를 통째로 기다리면 아래 ②의
        //   교착에 그대로 걸려 더 나빠진다.
        await epubBook.loaded.spine;

        // ✅ 이어 읽기 OR 처음부터 (표지)
        const target = (book.epubLocation && book.progress && book.progress > 2)
          ? book.epubLocation
          : ((epubBook.spine as any)?.items?.[0]?.href || undefined);
        console.log('7️⃣ Display 대상:', target || 'default');

        // ★ 딥QA 수정(2026-09-14) ②: rendition.display()는 내부적으로 book.opened가
        //   resolve될 때까지 큐에 걸린다(epubjs/lib/rendition.js:161 `q.enqueue(book.opened)`).
        //   book.opened가 끝내 resolve되지 않는 경우가 실제로 관측됐고(실측: 같은 파일이
        //   어떤 때는 열리고 어떤 때는 90초를 기다려도 iframe조차 안 생김), 그때 리더는
        //   "책을 불러오는 중…" 스피너에서 영영 못 빠져나왔다 — 사용자에겐 완전한 먹통이다.
        //   원인(epub.js 내부 resources 치환 체인)을 우리 쪽에서 확실히 제어할 수는 없으니,
        //   최소한 "무한 스피너"만은 만들지 않는다: 시간 안에 못 열면 에러 UI(새로고침)로 넘긴다.
        const DISPLAY_TIMEOUT_MS = 15000;
        const displayed = await Promise.race([
          rendition.display(target).then(() => true).catch((e: any) => {
            console.error('display() 실패:', e);
            return false;
          }),
          new Promise<boolean>((resolve) => setTimeout(() => resolve(false), DISPLAY_TIMEOUT_MS)),
        ]);

        if (!displayed) {
          // ※ 문구 주의: 이 상태에서 새로고침만으로 늘 복구되지는 않는다(실측 — 같은 탭에서
          //   반복 재현). 탭을 새로 여는 쪽이 확실했다. 되지도 않는 방법을 단정해 안내하지 않는다.
          throw new Error('책을 여는 데 너무 오래 걸립니다.\n새로고침해도 같으면 탭을 새로 열어 다시 시도해 주세요.');
        }
        console.log('   ✅ Display 완료!');

        setupEventListeners(rendition);

        console.log('═══════════════════════════════════════');
        console.log('🎉 EPUB 로드 성공!');
        console.log('═══════════════════════════════════════');

        setLoading(false);
        setError(null);

      } catch (err: any) {
        console.error('❌ EPUB 로드 실패:', err.message);
        setError(err.message || '알 수 없는 오류');
        setLoading(false);
      }
    };

    initEPUB();

    return () => {
      console.log('🧹 Cleanup...');
      window.epubNextPage = undefined;
      window.epubPrevPage = undefined;
      window.epubGoTo = undefined;
      window.epubGetCurrentText = undefined;
      window.epubSearchAllPages = undefined;
      window.epubCurrentPage = undefined;
      window.epubTotalPages = undefined;
      clearToc();
      renditionRef.current?.destroy();
      bookRef.current?.destroy();
      isInitializedRef.current = false;
    };
  // SPRINT 1.9 QA §1 부속 발견(긴급): 이 effect가 mount 시 정확히 2번 실행되는 진짜
  // 원인 — viewerReady를 의존성에 넣어뒀지만 effect 본문은 그 값을 전혀 쓰지 않고
  // viewerRef.current를 직접 체크한다(위 `if (!viewerRef.current) return`). React는
  // 커밋 단계에서 ref를 이미 붙여두므로 이 effect가 "처음" 실행될 때 이미
  // viewerRef.current는 존재한다 — 즉 viewerReady가 아직 false인 그 순간에도 로딩이
  // 시작된다(실측 로그로 확인: 1회차 실행 시 viewerReady=false). 그런데 바로 다음
  // 커밋에서 "Ensure viewerRef is available" effect(위, 의존성 배열 없음)가
  // setViewerReady(true)를 호출해 viewerReady가 실제로 false→true로 바뀌면,
  // 의존성 배열에 있는 viewerReady 값 변경 때문에 React가 이 effect의 cleanup을
  // 부르고 통째로 다시 실행한다 — EPUB 파일을 처음부터 다시 로드·파싱·렌더링한다
  // (로컬 프로덕션 빌드에서 3/3 100% 재현 확인). viewerReady는 effect 본문에서
  // 안 쓰이므로 의존성에서 뺀다 — ref 존재 여부는 이미 위에서 직접 검사하고 있어
  // 안전하다.
  }, [book.id, book.filePath, reopenAttempt]);

  // ✅ 이벤트 리스너 설정 (전체 페이지 기준)
  const setupEventListeners = (rendition: Rendition) => {
    rendition.on('relocated', (location: any) => {
      const cfi = location.start.cfi;
      const percentage = location.start.percentage * 100;

      // ✅ locations가 확정된 후에만 페이지 수 계산
      const total = window.epubTotalPages || 0;
      if (total > 0) {
        const page = Math.max(1, Math.round((percentage / 100) * total));
        window.epubCurrentPage = page;
        setCurrentPage(page);
        setTotalPages(total);
        console.log('📍 위치 변경:', page, '/', total, `(${percentage.toFixed(1)}%)`);
      } else {
        // locations 아직 준비 안됨 — percentage만 업데이트
        console.log('📍 위치 변경: (locations 생성 중...)', `${percentage.toFixed(1)}%`);
      }

      updateBook(book.id, {
        progress: Math.round(percentage),
        epubLocation: cfi,
        lastRead: new Date(),
        status: percentage >= 99 ? 'completed' : 'reading',
      });
      onLocationChange(cfi);
    });

    rendition.on('click', (event: MouseEvent) => {
      setShowSelectionMenu(false);
      const x = event.clientX;
      const width = window.innerWidth;
      if (x < width * 0.3) {
        rendition.prev();
      } else if (x > width * 0.7) {
        rendition.next();
      }
    });

    rendition.on('selected', (cfiRange: string, contents: any) => {
      const selection = contents.window.getSelection();
      if (!selection || selection.isCollapsed) {
        setShowSelectionMenu(false);
        return;
      }
      const text = selection.toString().trim();
      if (!text) {
        setShowSelectionMenu(false);
        return;
      }
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setSelectionMenuPos({ x: rect.left + rect.width / 2, y: rect.top - 10 });
      setSelectedText(text);
      setSelectedCfiRange(cfiRange);
      setShowSelectionMenu(true);
    });
  };

  const applyReaderSettings = (rendition: Rendition) => {
    const { fontSize, lineHeight, fontFamily, theme, preserveOriginalStyle } = readerSettings;

    if (preserveOriginalStyle) return;

    const fontMap: Record<string, string> = {
      'nanum-gothic': '"Nanum Gothic", sans-serif',
      'nanum-myeongjo': '"Nanum Myeongjo", serif',
      'gothic': '"Gothic A1", sans-serif',
      'serif-kr': '"Noto Serif KR", serif',
      'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    };

    const themeColorMap: Record<string, { color: string; background: string }> = {
      light: { color: '#1f2937', background: '#ffffff' },
      dark: { color: '#e5e5e5', background: '#1a1a1a' },
      black: { color: '#ffffff', background: '#000000' },
      sepia: { color: '#5c4a3a', background: '#f4ecd8' },
      green: { color: '#1b5e20', background: '#e8f5e9' },
      blue: { color: '#0d47a1', background: '#e3f2fd' },
      rose: { color: '#880e4f', background: '#fce4ec' },
      gray: { color: '#424242', background: '#f5f5f5' },
      midnight: { color: '#bfdbfe', background: '#0f172a' },
      forest: { color: '#bbf7d0', background: '#0d1f0d' },
      wine: { color: '#fecdd3', background: '#1a0a0a' },
      ocean: { color: '#a5f3fc', background: '#0a1628' },
    };

    const themeColors = themeColorMap[theme] || themeColorMap.light;

    rendition.themes.fontSize(`${fontSize}px`);
    rendition.themes.default({
      body: {
        'line-height': `${lineHeight} !important`,
        'font-family': `${fontMap[fontFamily]} !important`,
        color: `${themeColors.color} !important`,
        background: `${themeColors.background} !important`,
        'word-break': 'keep-all !important',
        'line-break': 'strict !important',
      },
      // Readium CSS §3.4: themes.default({body:{...}})는 출판사 CSS가 p/div 등
      // 태그를 직접 겨냥하면 무력해진다 — 같은 규칙을 자손 태그에도 명시적으로
      // 반복해 선택자 우선순위 싸움 자체를 없앤다.
      [TEXT_FLOW_SELECTOR]: {
        'line-height': `${lineHeight} !important`,
        'font-family': `${fontMap[fontFamily]} !important`,
        color: `${themeColors.color} !important`,
        'word-break': 'keep-all !important',
        'line-break': 'strict !important',
      },
      // Readium CSS §3.1 예외: keep-all은 URL·긴 토큰이 있는 요소에서 가로
      // 스크롤을 만들 수 있어 이 넷만 단어 중간 줄바꿈을 다시 허용한다.
      'pre, code, a[href], td': {
        'overflow-wrap': 'break-word !important',
      },
      // SPRINT 2.1-hotfix(PM 실측): epub.js의 자체 adjustImages()가 display()
      // 시점의 컨테이너 크기로 img에 max-height 인라인을 한 번 박아넣는데
      // (rendition.js:958), 그 직후 TOC 패널 자동 열림·하단 바 마운트로
      // 컨테이너가 더 줄어들어도 재계산이 안 된다(resize 핸들러는 columns()만
      // 다시 돌리고 adjustImages는 안 부른다) — 이미지가 그 시점엔 맞았던,
      // 지금은 너무 큰 인라인 max-height에 걸려 다음 컬럼으로 밀려나면서
      // 첫 컬럼이 빈 것처럼 보인다. resize 이벤트 훅(JS)보다 CSS로 아예
      // "컬럼 높이 기준으로 항상 다시 계산"하게 만드는 쪽을 골랐다 — 이번
      // 스프린트에서 override()/on('rendered') 재적용이 타이밍에 취약하다는
      // 걸 반복 확인했고(§4), CSS calc()는 이벤트를 안 타고 매 레이아웃마다
      // 브라우저가 알아서 다시 계산해 그 문제 자체가 없다. 100vh는 iframe
      // 안에서 컬럼 높이(=body height)와 같다. 40px는 epub.js 자체 상하
      // 패딩(padding-top/bottom 20px씩, Contents.columns() 확인), 2.5em은
      // figure 마진·줄상자 여유(PM 지정값).
      'body img, body svg': {
        'max-height': 'calc(100vh - 40px - 2.5em) !important',
      },
    });

    // SPRINT 2.1(Readium CSS §3.2, 줄 길이 제한) — max-width/margin/padding으로
    // "40em 폭의 읽기 좋은 한 컬럼"을 만드는 건 이번엔 넣지 않았다. 세 가지
    // 방법을 실측했고 전부 같은 벽에 부딪혔다: epub.js는 "본문 폭 = 컨테이너
    // 폭"이라는 전제로 각 섹션이 몇 "페이지"(=뷰포트 폭 단위)가 필요한지 미리
    // 계산해 iframe을 그만큼 넓게 만들고, next()/prev()는 그 iframe을 정확히
    // 뷰포트 폭만큼씩 옮겨 다음 페이지를 보여준다(Contents.columns(),
    // managers/views/iframe.js expand() — 원본 소스 확인). 우리가 본문이
    // "읽히는" 폭만 좁히면(어떤 방법으로든) 그 계산과 실제 렌더 사이에 틈이
    // 생겨 화면에 다음 페이지 내용이 함께/미리 보인다:
    //   ① max-width로 body 폭 자체를 줄임 → 뷰포트 하나에 좁아진 "페이지"가
    //      2~3개씩 나란히 보임(신문 멀티컬럼처럼). column-width/column-count로
    //      컬럼 수를 강제해도 무관하게 재현 — 원인이 컬럼 수가 아니라 페이지
    //      전환 스크롤 단위 자체이기 때문.
    //   ② margin:0 auto로 중앙 정렬 시도 → epub.js가 body에 margin:0
    //      !important를 강제하는데, themes.override()로 맞대응해봐도(body 폭이
    //      줄어든 상태라) epub.js의 자체 재계산(expand())이 우리 값을 계속
    //      되돌려 재적용↔재계산이 무한 진동했다(MutationObserver로 직접 확인).
    //   ③ body 폭은 그대로 두고 padding-left/right만 크게 줘서(box-sizing:
    //      border-box라 바깥 치수엔 안 더해짐) "읽기 폭"만 좁히는 절충안 →
    //      진동은 없었지만(폭을 안 건드리니 재계산 루프가 없다), epub.js가
    //      동시에 띄워두는 인접(다음) 페이지 iframe에는 우리 padding이 아직
    //      안 걸려 있어 화면 오른쪽에 다음 페이지의 좁혀지지 않은 본문이
    //      살짝 비쳐 보였다(실측 스크린샷 확인).
    // 세 경우 다 "이 함수(스타일 주입) 하나"로 못 막는 구조적 문제라 판단해
    // 여기서 멈췄다. word-break/line-break(위)는 이 문제와 무관하게 독립적으로
    // 잘 동작한다 — 그게 이번 스프린트의 핵심 민원(단어 중간 줄바꿈)이었다.
    // 줄 길이 제한 자체는 SPRINT 2.2 후보로 남긴다(§8) — epub.js의 페이지-폭
    // 계산 쪽(매니저/레이아웃 레벨)을 같이 손보지 않는 한 CSS 단독으로는
    // 안전하게 안 풀린다는 게 이번에 실측으로 확인된 결론이다.
  };

  useEffect(() => {
    if (renditionRef.current) {
      applyReaderSettings(renditionRef.current);
    }
  }, [readerSettings]);

  const handleHighlight = (color: HighlightColor) => {
    if (!selectedText || !selectedCfiRange) return;
    addHighlight({
      id: crypto.randomUUID(),
      bookId: book.id,
      text: selectedText,
      color,
      cfiRange: selectedCfiRange,
      createdAt: new Date(),
    });
    if (renditionRef.current) {
      const colorMap: Record<HighlightColor, string> = {
        yellow: '#fff59d', green: '#a5d6a7', blue: '#90caf9', pink: '#f48fb1', purple: '#ce93d8',
      };
      renditionRef.current.annotations.highlight(selectedCfiRange, {}, () => {}, 'hl', { fill: colorMap[color] });
    }
    setShowSelectionMenu(false);
  };

  const handleNote = () => {
    setShowSelectionMenu(false);
    setShowNoteModal(true);
  };

  const handleSaveNote = (noteContent: string) => {
    if (!selectedText || !selectedCfiRange) return;
    addNote({
      id: crypto.randomUUID(),
      bookId: book.id,
      text: selectedText,
      content: noteContent,
      cfiRange: selectedCfiRange,
      createdAt: new Date(),
    });
  };

  const handleAskAI = (action: 'ask' | 'summarize' | 'translate' | 'explain') => {
    setAIAction(action);
    setShowSelectionMenu(false);
    setShowAIAssistant(true);
  };

  const progress = totalPages > 0 ? Math.min(100, Math.round((currentPage / totalPages) * 100)) : 0;

  const goToPrevPage = () => renditionRef.current?.prev();
  const goToNextPage = () => renditionRef.current?.next();

  // ✅ 진행바 클릭/드래그로 해당 위치 점프
  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!locationsReady || !bookRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, clickX / rect.width));
    const cfi = bookRef.current.locations.cfiFromPercentage(ratio);
    if (cfi && renditionRef.current) {
      renditionRef.current.display(cfi);
    }
  };


  return (
    <div className="flex flex-col h-full">
      {/* EPUB Content */}
      <div className="flex-1 relative overflow-hidden">
        <div
          {...swipeHandlers}
          ref={viewerRef}
          className="epub-viewer w-full h-full"
          style={{ touchAction: 'pan-y', minHeight: '400px' }}
        />

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">책을 불러오는 중...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900 p-4">
          <div className="text-center text-red-600 max-w-md">
            <p className="whitespace-pre-line mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
            >
              페이지 새로고침
            </button>
          </div>
        </div>
      )}

      {showSelectionMenu && (
        <TextSelectionMenu
          x={selectionMenuPos.x}
          y={selectionMenuPos.y}
          selectedText={selectedText}
          onClose={() => setShowSelectionMenu(false)}
          onHighlight={handleHighlight}
          onNote={handleNote}
          onAskAI={handleAskAI}
        />
      )}

      {showNoteModal && (
        <NoteModal
          selectedText={selectedText}
          onSave={handleSaveNote}
          onClose={() => setShowNoteModal(false)}
        />
      )}

      {showAIAssistant && (
        <AIAssistant
          selectedText={selectedText}
          action={aiAction}
          onClose={() => setShowAIAssistant(false)}
        />
      )}
      </div>

      {/* 하단 네비게이션 — SPRINT 2.1-hotfix(PM 실측): 예전엔 !loading일 때만
          렌더돼 rendition.display() 시점(로딩 중)엔 이 footer가 없어 컨테이너가
          더 컸다. epub.js의 adjustImages()는 그 display 시점 크기로 이미지
          max-height를 한 번만 굳히는데, footer가 로드 완료 후 뒤늦게
          마운트되면서 컨테이너가 줄어들어도 그 값이 재계산되지 않아(resize
          핸들러는 컬럼 폭만 다시 계산) 이미지가 다음 컬럼으로 밀려나 첫
          컬럼이 비어 보였다. footer를 처음부터(로딩 중에도) 렌더해 display()가
          측정하는 컨테이너 크기를 최종 크기와 같게 만든다 — 안의 내용은
          이미 locationsReady/loading 상태를 개별적으로 방어하고 있어(진행바
          "페이지 계산 중..." 폴백, prev/next는 renditionRef가 없으면 옵셔널
          체이닝으로 no-op) 로딩 중에 보여도 깨지지 않는다. */}
      {!error && (
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-2 py-3">
          {/* ◀ [========진행바========] ▶  한 줄 배치 */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevPage}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              title="이전 페이지"
            >
              <FiChevronLeft size={24} />
            </button>

            {/* 진행바 — 최대 폭 (클릭으로 점프) */}
            <div className="flex-1 min-w-0">
              <div
                className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer relative group"
                onClick={handleProgressBarClick}
                title={locationsReady ? `${currentPage} / ${totalPages} 페이지 — 클릭하여 이동` : '페이지 계산 중...'}
              >
                <div
                  className="h-full gradient-primary rounded-full transition-all duration-300 pointer-events-none"
                  style={{ width: `${progress}%` }}
                />
                {locationsReady && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-blue-500 rounded-full shadow-lg group-hover:scale-125 transition-transform pointer-events-none"
                    style={{ left: `calc(${progress}% - 10px)` }}
                  />
                )}
              </div>
            </div>

            <button
              onClick={goToNextPage}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              title="다음 페이지"
            >
              <FiChevronRight size={24} />
            </button>
          </div>

          {/* 페이지 정보 */}
          <div className="flex items-center justify-center gap-3 mt-1">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {locationsReady ? `${currentPage} / ${totalPages}` : '페이지 계산 중...'}
            </span>
            {locationsReady && (
              <span className="text-sm text-gray-500">({progress}%)</span>
            )}
          </div>

        </footer>
      )}
    </div>
  );
};

export default EPUBViewer;
